import { searchClient, locale, history, simple } from './algolia-config.js';
import { templates } from './algolia-instantsearch.min.js';

const { connectSortBy, connectCurrentRefinements, connectClearRefinements } = instantsearch.connectors;

const SEARCH_THROTTLE_DELAY = 500;
const OBSERVER_ROOT_MARGIN = '200px';
const OBSERVER_THRESHOLD = 0.01;

const supplierMap = new Map((window.suppliers || []).map(s => [s.supplierCode, s.name]));

let timerId;
let firstFacetOpened = false;

const useAlgoliaRouting =
  window.location.pathname === '/search' || typeof window.collection_id !== 'undefined';

const search = instantsearch({
  indexName: 'shopify_products',
  searchClient,
  routing: useAlgoliaRouting
    ? {
        router: history(),
        stateMapping: simple(),
      }
    : false,
});

if (window.collection_id) {
  search.addWidgets([
    instantsearch.widgets.configure({
      filters: `collection_ids: ${window.collection_id}`,
    })
  ]);
}

const createFacetWidget = (config) => {
  return instantsearch.widgets.dynamicWidgets({
        container: config.selector,
        facets: ['*'],
        maxValuesPerFacet: 1000,

        fallbackWidget: ({ container, attribute }) => {
            const { searchable, type, title } = getConfig(attribute);
            const transformSupplierCode = (items) => {
              return items.map(item => {
                const newName = supplierMap.get(item.value);
  
                if (newName && item.label !== newName) {
                  return {
                    ...item,
                    label: newName,
                    highlighted: newName 
                  };
                }
                return item;
              });
            }

            if (type === 'slider') {
              return instantsearch.widgets.panel({
                templates: { header: config.header(title) },
                hidden({ range }) {
                  return !range || range.min === range.max;
                },
                cssClasses: config.widgetRootClasses
              })(instantsearch.widgets.rangeSlider)({
                container,
                attribute, 
                precision: 1,
                cssClasses: config.sliderFacetClasses
              })
            }

            return instantsearch.widgets.panel({
              templates: { header: config.header(title) },
              hidden({ items }) {
                return items.length === 0;
              },
              cssClasses: config.widgetRootClasses
            })(instantsearch.widgets.refinementList)({
              container,
              attribute,
              transformItems: attribute === 'meta.custom.supplierCode' ? transformSupplierCode : undefined,
              limit: searchable ? 1000 : 10,
              showMore: !searchable,
              showMoreLimit: 1000,
              sortBy: ['name:asc'],
              searchable: searchable,
              searchablePlaceholder: `${document.querySelector('#algolia-filter').getAttribute('data-search-text')}...`,
              cssClasses: searchable ? config.searchableFacetClasses : config.refinementListClasses
            });
        },

        widgets: [],
        transformItems(attributes, { results }) {
          return attributes;
        },
    })
}

const configDesktop = {
  selector: '#algolia-filter',
  header: (title) => {
    return `
      <summary class="facets__summary">
        <span class="h6">
          ${title}
        </span>
        <div class="facets__summary-icon"></div>
      </summary>
    `;
  },
  widgetRootClasses: {
    root: ['facets__disclosure-vertical', 'js-filter']
  },
  refinementListClasses: {
    root: ['facet__model-filter', 'facets__display-vertical'],
    list: ['facets-layout-list', 'facets__list--vertical', 'list-unstyled', 'no-js-hidden'],
    item: ['list-menu__item', 'facets__item'],
    label: ['facets__label', 'facet-checkbox'],
    labelText: ['facet-checkbox__text'],
    selectedItem: ['active'],
    showMore: ['button-show-more', 'link', 'underlined-link', 'no-js-hidden']
  },
  sliderFacetClasses: {
    root: ['facet__model-filter', 'facets__display-vertical']
  },
  searchableFacetClasses: {
    root: ['facet__model-filter facets__display-vertical'],
    list: ['facets__machine-model-select', 'facets-layout-list', 'facets__list--vertical', 'list-unstyled', 'no-js-hidden'],
    item: ['list-menu__item', 'facets__item'],
    label: ['facets__label', 'facet-checkbox'],
    labelText: ['facet-checkbox__text'],
    selectedItem: ['active'],
    showMore: ['button-show-more', 'link', 'underlined-link', 'no-js-hidden'],
    searchableForm: ['field'],
    searchableInput: ['field__input', 'facets__machine-model-filter'],
    searchableSubmit: ['search__button', 'field__button', 'search-button'],
    searchableReset: ['field__button', 'search__button', 'reset-button', 'icon-reset']
  }
};

const configMobile = {
  selector: '#algolia-filter-mobile',
  header: (title) => {
    return `
      <summary class="mobile-facets__summary focus-inset">
        <div>
          <span class="mobile-facets__summary-label h6">${title}</span>
          <span class="mobile-facets__arrow no-js-hidden"><span class="facet-button"></span></span>
          <noscript>
            <svg aria-hidden="true" focusable="false" class="icon icon-caret {% if class != blank %} {{ class }}{% endif %}" viewBox="0 0 10 6">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor">
            </svg>
          </noscript>
        </div>
      </summary>
    `;
  },
  widgetRootClasses: {
    root: ['mobile-facets__details', 'js-filter']
  },
  refinementListClasses: {
    root: ['mobile-facets__submenu', 'gradient'],
    list: ['facets-layout-list', 'mobile-facets__list', 'list-unstyled'],
    item: ['mobile-facets__item', 'list-menu__item'],
    label: ['facets__label', 'facet-checkbox', 'mobile-facets__label'],
    labelText: ['facet-checkbox__text'],
    selectedItem: ['active'],
    showMore: ['button-show-more', 'link', 'underlined-link', 'no-js-hidden']
  },
  sliderFacetClasses: {
    root: ['mobile-facets__submenu', 'gradient']
  },
  searchableFacetClasses: {
    root: ['mobile-facets__submenu', 'gradient', 'facet__model-filter', 'mobile'],
    list: ['facets__machine-model-select', 'facets-layout-list', 'mobile-facets__list', 'list-unstyled'],
    item: ['mobile-facets__item', 'list-menu__item'],
    label: ['facets__label', 'facet-checkbox', 'mobile-facets__label'],
    labelText: ['facet-checkbox__text'],
    selectedItem: ['active'],
    showMore: ['button-show-more', 'link', 'underlined-link', 'no-js-hidden'],
    searchableForm: ['field'],
    searchableInput: ['field__input', 'facets__machine-model-filter'],
    searchableSubmit: ['search__button', 'field__button', 'search-button'],
    searchableReset: ['field__button', 'search__button', 'reset-button', 'icon-reset']
  }
};

const renderSortBySelect = (renderOptions, isFirstRender) => {
  const { currentRefinement, refine, widgetParams } = renderOptions;
  const { items, containerSelector } = widgetParams;

  if (!items || !containerSelector) {
    console.warn('SortBy Widget: Items oder Container fehlen.');
    return;
  }

  const container = document.querySelector(containerSelector);
  if (!container) return;

  container.innerHTML = items
    .map((item) => `
      <div class="sort-by--item ${item.value === currentRefinement ? 'selected' : ''}">
        <label for="sort_by-${item.value}">
          <input
            class="sr-only selection-element"
            name="sort_by"
            id="sort_by-${item.value}"
            type="radio"
            value="${item.value}"
          >
          ${item.label}
        </label>
      </div>
    `)
    .join('');

  if (isFirstRender) {
    container.addEventListener('change', (event) => {
      const input = event.target;
      if (input.tagName === 'INPUT' && input.type === 'radio' && input.name === 'sort_by') {
        refine(input.value);
      }
    });
  }
};

const customSortBySelect = instantsearch.connectors.connectSortBy(renderSortBySelect);

const renderAlgoliaCurrentRefinements = (renderOptions) => {
  const { items, refine, widgetParams } = renderOptions;
  const wrapper = widgetParams.container;

  if (!wrapper) return;

  const clearFilterText = wrapper.getAttribute('data-clear-filter-text');
  const iconCloseSmall = wrapper.getAttribute('data-icon-close-small');

  wrapper.querySelectorAll('.algolia-refinement-pill').forEach((pill) => pill.remove());

  const refinementsList = items.flatMap((item) => item.refinements);
  const hasRefinements = refinementsList.length > 0;

  wrapper.classList.toggle('hidden', !hasRefinements);

  if (!hasRefinements) return;

  refinementsList.forEach((refinement, index) => {
    const pill = document.createElement('facet-remove');
    const label = refinement.attribute === 'meta.custom.supplierCode'
      ? (supplierMap.get(refinement.value) ?? refinement.label)
      : refinement.label;

    pill.className = 'algolia-refinement-pill';
    pill.innerHTML = `
      <a href="#" class="active-facets__button active-facets__button--light" role="button" tabindex="0">
        <span class="active-facets__button-inner">
          <span class="active-facets__button-inner-text">${label}</span>
          ${iconCloseSmall}
          <span class="visually-hidden">${clearFilterText}</span>
        </span>
      </a>
    `;

    pill.querySelector('a').addEventListener('click', (event) => {
      event.preventDefault();
      refine(refinementsList[index]);
    });

    wrapper.appendChild(pill);
  });
};

const customCurrentRefinements = connectCurrentRefinements(renderAlgoliaCurrentRefinements);

const renderDesktopClearRefinements = (renderOptions, isFirstRender) => {
  const { refine, widgetParams } = renderOptions;
  const trigger = widgetParams.container;

  if (!trigger || !isFirstRender) return;

  trigger.addEventListener('click', (event) => {
    event.preventDefault();
    refine();
  });
};

const customDesktopClearRefinements = connectClearRefinements(renderDesktopClearRefinements);

search.on('render', () => {
  registerProductCardsForObserver();

  if (firstFacetOpened) return;

  const firstPanel = document.querySelector('.ais-Panel.facets__disclosure-vertical.js-filter');

  if (firstPanel) {
    firstPanel.setAttribute('open', '');
    firstFacetOpened = true;
  }
});

const productObserver = new IntersectionObserver(
  (entries, observer) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const item = entry.target;
        observer.unobserve(item);
        fetchCardData(item);
      }
    });
  },
  { rootMargin: OBSERVER_ROOT_MARGIN, threshold: OBSERVER_THRESHOLD }
);

const registerProductCardsForObserver = () => {
  const placeholderList = document.querySelectorAll('.algolia-product-placeholder:not(.loaded):not(.fetching):not(.observed)');

  placeholderList.forEach((item) => {
    item.classList.add('observed');
    productObserver.observe(item);
  });
};

const fetchCardData = async (item) => {
  const handle = item.getAttribute('data-product-handle');
  const variantID = item.getAttribute('data-variant-id');
  if (!handle || !variantID) return;

  try {
    item.classList.add('fetching');
    const response = await fetch(`${locale != 'de' ? `/${locale}` : ''}/products/${handle}?variant=${variantID}&view=card`);

    if (!response.ok) throw new Error('Product not found');
    
    const html = await response.text();
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const productCard = tempDiv.querySelector('.card-wrapper');

    if (document.body.contains(item)) {
      item.innerHTML = productCard
        ? productCard.outerHTML
        : html;

      item.classList.add('loaded');
      item.classList.remove('fetching');

      const spinner = item.querySelector('.loading-placeholder');

      if (spinner) spinner.remove();
    }
  } catch (error) {
    console.error(`Error fetching product card for ${handle}:`, error);
    item.classList.remove('fetching');
  }
};

const getConfig = (handle) => {
  return window.algolia_facets.find((element) => element.name === handle) ?? false;
}

document.addEventListener('click', (event) => {
  const summary = event.target.closest('summary.facets__summary, summary.mobile-facets__summary');
  if (!summary) return;

  const panel = summary.closest('.ais-Panel, .mobile-facets__details');
  if (!panel) return;

  panel.hasAttribute('open') ? panel.removeAttribute('open') : panel.setAttribute('open', '');
});

const filterButton = document.getElementById('vertical-filter-button');
const facets = document.getElementById('facets-wrapper');

const toggleFacets = () => {
  const isOpen = facets.classList.toggle('js-active');

  isOpen ? filterButton.setAttribute('open', '') : filterButton.removeAttribute('open');
  updateFocusableElements(isOpen);
};

const updateFocusableElements = (isOpen) => {
  const focusables = facets.querySelectorAll('input, button, select, a, textarea, summary');

  focusables.forEach((el) => {
    el.tabIndex = isOpen ? 0 : -1;
  });
};

filterButton.addEventListener('click', toggleFacets);

filterButton.addEventListener('keydown', (event) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    toggleFacets();
  }
});

document.querySelector('#algolia-clear-refinements').addEventListener('click', (e) => {
  e.target.closest('.mobile-facets__wrapper').querySelector('summary').click();
});

search.addWidgets([
  customSortBySelect({
    containerSelector: '#sort-by-algolia',
    items: window.sort_by_config,
  }),

  instantsearch.widgets.sortBy({
    container: '#SortBy-mobile-select',
    items: window.sort_by_config,
    cssClasses: {
      select: ['select__select']
    }
  }),

  instantsearch.widgets.clearRefinements({
    container: '#algolia-clear-refinements',
    cssClasses: {
      button: ['mobile-facets__clear', 'underlined-link'],
      disabledButton: ['disabled']
    },
    templates: {
      resetLabel: document.getElementById('algolia-clear-refinements').getAttribute('data-text')
    }
  }),

  customDesktopClearRefinements({
    container: document.getElementById('algolia-clear-refinements-desktop-trigger')
  }),

  customCurrentRefinements({
    container: document.querySelector('#algolia-active-facets-wrapper')
  }),

  instantsearch.widgets.stats({
    container: '#ProductCountDesktop',
    templates: {
      text(data) {
        const hasResults = document.getElementById('hits-has-results');
        const noResults = document.getElementById('hits-no-results');
        const noResultText = document.getElementById('hits-no-result-text');
        const productCount = document.getElementById('ProductCountDesktop');
        let resultsText = '';

        if (data.hasManyResults) {
          hasResults.classList.remove('hidden');
          noResults.classList.add('hidden');
          resultsText = productCount.getAttribute('data-other-result').replace('%amount%', data.nbHits);
        } else if (data.hasOneResult) {
          hasResults.classList.remove('hidden');
          noResults.classList.add('hidden');
          resultsText = productCount.getAttribute('data-one-result').replace('%amount%', data.nbHits);
        } else {
          hasResults.classList.add('hidden');
          noResults.classList.remove('hidden');
          noResultText.innerHTML = noResultText.innerHTML.replace('%query%', data.query);
          resultsText = productCount.getAttribute('data-other-result').replace('%amount%', data.nbHits);
        }

        document.querySelectorAll('.algolia-hits__count').forEach((item) => {
          item.innerHTML = resultsText;
        });
        
        return resultsText;
      }
    }
  }),

  instantsearch.widgets.pagination({
    container: '#algolia-pagination',
    scrollTo: '#algolia-searchbox',
    showFirst: false,
    showLast: false,
    cssClasses: {
      list: ['pagination__list', 'list-unstyled', 'space-between'],
      link: ['pagination__item', 'pagination__item--link', 'link', 'link-underline'],
      previousPageItem: ['pagination__item--prev pagination__item-arrow link motion-reduce active button--animate-default'],
      nextPageItem: ['pagination__item--next pagination__item-arrow link motion-reduce activ button--animate-default'],
      selectedItem: ['pagination__item--current', 'light'],
      disabledItem: ['disabled']
    },
    templates: {  
      previous: (data, { html }) => html`
        <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false" class="icon icon-short-arrow" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6.99823 0.137207L5.78898 1.34677L10.5745 6.14211H0.137207V7.85779H10.5745L5.78898 12.6531L6.99823 13.8627L13.8593 6.99995L6.99823 0.137207Z" fill="currentColor"/>
        </svg>
      `,

      next: (data, { html }) => html`
        <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true" focusable="false" class="icon icon-short-arrow" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M6.99823 0.137207L5.78898 1.34677L10.5745 6.14211H0.137207V7.85779H10.5745L5.78898 12.6531L6.99823 13.8627L13.8593 6.99995L6.99823 0.137207Z" fill="currentColor"/>
        </svg>
      `
    }
  }),

  instantsearch.widgets.searchBox({
    container: '#algolia-searchbox',
    cssClasses: {
      form: ['field'],
      input: ['field__input', 'collection__input'],
      submit: ['search__button', 'field__button', 'main-search'],
      reset: ['reset__button', 'field__button', 'main-search-reset', 'hidden']
    },
    templates: {
      submit({ cssClasses }, { html }) {
        return html`<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none"><g clip-path="url(#clip0_80_46)"><path d="M13 6.5C13 7.93437 12.5344 9.25938 11.75 10.3344L15.2063 13.7937L15.9156 14.5L14.5 15.9156L13.7937 15.2094L10.3344 11.75C9.25938 12.5375 7.93437 13 6.5 13C2.90937 13 0 10.0906 0 6.5C0 2.90937 2.90937 0 6.5 0C10.0906 0 13 2.90937 13 6.5ZM6.5 11C7.09095 11 7.67611 10.8836 8.22208 10.6575C8.76804 10.4313 9.26412 10.0998 9.68198 9.68198C10.0998 9.26412 10.4313 8.76804 10.6575 8.22208C10.8836 7.67611 11 7.09095 11 6.5C11 5.90905 10.8836 5.32389 10.6575 4.77792C10.4313 4.23196 10.0998 3.73588 9.68198 3.31802C9.26412 2.90016 8.76804 2.56869 8.22208 2.34254C7.67611 2.1164 7.09095 2 6.5 2C5.90905 2 5.32389 2.1164 4.77792 2.34254C4.23196 2.56869 3.73588 2.90016 3.31802 3.31802C2.90016 3.73588 2.56869 4.23196 2.34254 4.77792C2.1164 5.32389 2 5.90905 2 6.5C2 7.09095 2.1164 7.67611 2.34254 8.22208C2.56869 8.76804 2.90016 9.26412 3.31802 9.68198C3.73588 10.0998 4.23196 10.4313 4.77792 10.6575C5.32389 10.8836 5.90905 11 6.5 11Z" fill="currentColor"/></g><defs><clipPath id="clip0_80_46"><rect width="16" height="16" fill="currentColor"/></clipPath></defs></svg>`;
      },
      reset({ cssClasses }, { html }) {
        return html`<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M11.4 1.8L12 1.2L10.8 0.00352936L10.2 0.603529L6 4.80353L1.8 0.6L1.2 0L0.00352943 1.2L0.603529 1.8L4.80353 6L0.6 10.2L0 10.8L1.2 11.9965L1.8 11.3965L6 7.19647L10.2 11.4L10.8 12L11.9965 10.8L11.3965 10.2L7.19647 6L11.4 1.8Z" fill="black"/></svg>`;
      }
    },
    queryHook(query, refine) {
      clearTimeout(timerId);
      timerId = setTimeout(() => refine(query), SEARCH_THROTTLE_DELAY);
    },
    ignoreCompositionEvents: false
  }),

  instantsearch.widgets.hits({
    container: '#algolia-product-grid',
    cssClasses: {
      list: ['grid product-grid', document.querySelector('#algolia-product-grid').getAttribute('data-columns')],
      item: ['grid__item', 'scroll-trigger', 'animate--fade-in']
    },
    templates: {
      item(hit, { html, components }) {
        return `
          <div
            class="algolia-product-placeholder"
            data-product-handle="${hit.handle}"
            data-variant-id="${hit.objectID}"
          >
            <div class="loading-placeholder">
              <div class="loading-spinner"></div>
            </div>
          </div>
        `;
      }
    },
  }),

  createFacetWidget(configDesktop),
  createFacetWidget(configMobile)
]);

search.start();
